import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { fetchInquiries, fetchProjects } from '@/lib/api';

type Stats = {
  views: number;
  projects: number;
  pending: number;
  review: number;
  completed: number;
};

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats>({
    views: 1420,
    projects: 0,
    pending: 0,
    review: 0,
    completed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError('');
    try {
      const [inquiriesData, projectsData] = await Promise.all([
        fetchInquiries(),
        fetchProjects(),
      ]);

      const pending = inquiriesData.filter((i) => (i.status || 'pending').toLowerCase() === 'pending').length;
      const review = inquiriesData.filter((i) => (i.status || '').toLowerCase() === 'review').length;
      const completed = inquiriesData.filter((i) => (i.status || '').toLowerCase() === 'completed').length;

      setStats({
        views: 1420,
        projects: projectsData.length,
        pending,
        review,
        completed,
      });
    } catch {
      setError('Unable to refresh latest metrics.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(true);

    const handleUpdate = () => {
      fetchStats(false);
    };

    window.addEventListener('inquiries_updated', handleUpdate);
    window.addEventListener('projects_updated', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('inquiries_updated', handleUpdate);
      window.removeEventListener('projects_updated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [fetchStats]);

  const cards = [
    { label: 'Total Views', value: stats.views.toLocaleString(), icon: 'ri-user-3-line', accent: 'from-neon-600/30 to-neon-400/30' },
    { label: 'Projects', value: String(stats.projects), icon: 'ri-briefcase-4-line', accent: 'from-neon-600/30 to-neon-400/30' },
    { label: 'Pending Inquiries', value: String(stats.pending), icon: 'ri-mail-unread-line', accent: 'from-emerald-600/30 to-emerald-400/30' },
    { label: 'In Review', value: String(stats.review), icon: 'ri-eye-line', accent: 'from-amber-600/30 to-amber-400/30' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-sm text-white/50 mt-1">Overview of your website performance and inquiries.</p>
        </div>
        <div className="text-xs font-mono text-neon-400 bg-neon-500/10 px-3 py-1.5 rounded-lg border border-neon-500/20">
          <i className="ri-time-line mr-1" /> {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => fetchStats()} className="text-red-300 hover:text-red-200 font-medium">
            Retry
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((s) => (
          <div key={s.label} className="glass rounded-xl p-5 border border-white/5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br ${s.accent} text-neon-400`}>
                <i className={s.icon} />
              </div>
            </div>
            <div className="font-display text-2xl font-bold text-white">
              {loading ? '—' : s.value}
            </div>
            <div className="text-xs text-white/50 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Inquiries Overview + Quick Actions */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Inquiries Overview */}
        <div className="lg:col-span-2 glass rounded-xl p-6 border border-white/5">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-semibold text-lg text-white">Inquiries Overview</h2>
            <Link to="/admin/inquiries" className="text-xs text-neon-400 hover:text-neon-300 transition">
              View All <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { label: 'Pending', value: stats.pending, icon: 'ri-mail-unread-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
              { label: 'In Review', value: stats.review, icon: 'ri-eye-line', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
              { label: 'Completed', value: stats.completed, icon: 'ri-check-double-line', color: 'text-neon-300', bg: 'bg-neon-500/10 border-neon-500/20' },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border p-5 ${item.bg}`}>
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${item.bg} ${item.color} mb-3`}>
                  <i className={item.icon} />
                </div>
                <div className="font-display text-2xl font-bold text-white">{loading ? '—' : item.value}</div>
                <div className="text-xs text-white/50 mt-1">{item.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/5">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-neon-500/10 text-neon-400 shrink-0">
              <i className="ri-history-line" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-white">Completed inquiries go to History</div>
              <div className="text-xs text-white/50">Review and permanently delete completed records.</div>
            </div>
            <Link to="/admin/history" className="text-xs text-neon-400 hover:text-neon-300 transition whitespace-nowrap">
              View History
            </Link>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="glass rounded-xl p-6 border border-white/5">
          <h2 className="font-display font-semibold text-lg text-white mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link to="/admin/portfolio" className="flex items-center gap-3 p-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-neon-500/30 transition">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-neon-500/10 text-neon-400">
                <i className="ri-add-line text-lg" />
              </div>
              <div>
                <div className="text-sm font-medium text-white">Add Project</div>
                <div className="text-xs text-white/50">To portfolio</div>
              </div>
            </Link>
            <Link to="/admin/inquiries" className="flex items-center gap-3 p-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-neon-500/30 transition">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-neon-500/10 text-neon-400">
                <i className="ri-mail-check-line text-lg" />
              </div>
              <div>
                <div className="text-sm font-medium text-white">Review Inquiries</div>
                <div className="text-xs text-white/50">Contact queries</div>
              </div>
            </Link>
            <Link to="/admin/history" className="flex items-center gap-3 p-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-neon-500/30 transition">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-neon-500/10 text-neon-400">
                <i className="ri-history-line text-lg" />
              </div>
              <div>
                <div className="text-sm font-medium text-white">View History</div>
                <div className="text-xs text-white/50">Completed inquiries</div>
              </div>
            </Link>
            <Link to="/" className="flex items-center gap-3 p-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-neon-500/30 transition">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-neon-500/10 text-neon-400">
                <i className="ri-external-link-line text-lg" />
              </div>
              <div>
                <div className="text-sm font-medium text-white">View Website</div>
                <div className="text-xs text-white/50">Live preview</div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}